import React, { useState, useEffect, useCallback } from "react";
import { Select, Textarea, Text } from "@chakra-ui/react";
import Modal from "newui/components/ui/Modal/Modal";
import { RevisionFilePicker, revisionFileSchema } from "./RevisionFilePicker";
import type {
	EvaluationRevisionSupplier,
	RevisionUploadType,
	UploadRevisionPayload,
} from "../types/revision-review.type";

const MAX_COMMENT_LENGTH = 5000;

interface UploadRevisionModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (payload: UploadRevisionPayload) => void;
	isLoading: boolean;
	suppliers: EvaluationRevisionSupplier[];
}

interface FormState {
	rfxInviteId: number | null;
	revisionType: RevisionUploadType;
	comment: string;
	files: File[];
}

const REVISION_TYPE_OPTIONS: { value: RevisionUploadType; label: string }[] = [
	{ value: "quote", label: "Quote" },
	{ value: "questions", label: "Questions" },
	{ value: "submission_documents", label: "Submission Documents" },
];

const initialFormState: FormState = {
	rfxInviteId: null,
	revisionType: "quote",
	comment: "",
	files: [],
};

export const UploadRevisionModal: React.FC<UploadRevisionModalProps> = ({
	isOpen,
	onClose,
	onSubmit,
	isLoading,
	suppliers,
}) => {
	const [form, setForm] = useState<FormState>(initialFormState);
	const [fileError, setFileError] = useState<string>("");
	const [commentError, setCommentError] = useState<string>("");

	useEffect(() => {
		if (isOpen) {
			setForm({
				...initialFormState,
				rfxInviteId: suppliers.length > 0 ? suppliers[0].id : null,
			});
			setFileError("");
			setCommentError("");
		}
	}, [isOpen, suppliers]);

	const handleFilesChange = useCallback((files: File[]) => {
		setForm((prev) => ({ ...prev, files }));
		setFileError("");
	}, []);

	const validateFiles = useCallback(async (files: File[]): Promise<string> => {
		try {
			await revisionFileSchema.validate(files);
			return "";
		} catch (err) {
			if (err instanceof Error) {
				return err.message;
			}
			return "Invalid files";
		}
	}, []);

	const handleSubmit = useCallback(async () => {
		if (!form.rfxInviteId) {
			return;
		}

		if (form.comment.length > MAX_COMMENT_LENGTH) {
			setCommentError(`Comment must be ${MAX_COMMENT_LENGTH} characters or less`);
			return;
		}
		setCommentError("");

		const validationError = await validateFiles(form.files);
		if (validationError) {
			setFileError(validationError);
			return;
		}

		onSubmit({
			rfx_invite_id: form.rfxInviteId,
			revision_type: form.revisionType,
			comment: form.comment || undefined,
			files: form.files,
		});
	}, [form, onSubmit, validateFiles]);

	const isValid =
		form.rfxInviteId !== null && form.files.length > 0 && !isLoading;

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Upload Revision"
			size="lg"
			modalType="standard"
			iconType="create"
			closeOnEsc
			closeOnOverlayClick={false}
			primaryButton={{
				text: isLoading ? "Uploading..." : "Upload Revision",
				onClick: handleSubmit,
				btntype: "primary",
				disabled: !isValid,
			}}
			secondaryButton={{
				text: "Cancel",
				onClick: onClose,
			}}
		>
			<div className="space-y-4">
				<div>
					<Text className="mb-1 text-sm font-medium text-gray-700">
						Supplier
					</Text>
					<Select
						value={form.rfxInviteId ?? ""}
						onChange={(e) =>
							setForm((prev) => ({
								...prev,
								rfxInviteId: Number(e.target.value),
							}))
						}
						size="sm"
						data-testid="supplier-select"
					>
						{suppliers.map((supplier) => (
							<option key={supplier.id} value={supplier.id}>
								{supplier.name}
							</option>
						))}
					</Select>
				</div>

				<div>
					<Text className="mb-1 text-sm font-medium text-gray-700">
						Revision Type
					</Text>
					<Select
						value={form.revisionType}
						onChange={(e) =>
							setForm((prev) => ({
								...prev,
								revisionType: e.target.value as RevisionUploadType,
							}))
						}
						size="sm"
						data-testid="revision-type-select"
					>
						{REVISION_TYPE_OPTIONS.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</Select>
				</div>

				<div>
					<Text className="mb-1 text-sm font-medium text-gray-700">
						Comment (optional)
					</Text>
					<Textarea
						value={form.comment}
						onChange={(e) => {
							setForm((prev) => ({ ...prev, comment: e.target.value }));
							setCommentError("");
						}}
						placeholder="Add a comment about this revision..."
						size="sm"
						rows={3}
						maxLength={MAX_COMMENT_LENGTH}
						data-testid="comment-textarea"
					/>
					{commentError && (
						<Text className="mt-1 text-xs text-red-500" data-testid="comment-error">
							{commentError}
						</Text>
					)}
				</div>

				<div>
					<Text className="mb-1 text-sm font-medium text-gray-700">
						Files
					</Text>
					<RevisionFilePicker
						files={form.files}
						onChange={handleFilesChange}
						error={fileError}
					/>
				</div>
			</div>
		</Modal>
	);
};
